import { Modal, Box, Button, IconButton, Autocomplete, TextField } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { updateCombo } from "../../services/designation/updateDesignation";
import { deleteCombo } from "../../services/designation/deleteDesignation";
import { COLORS, buttonSx } from "../../theme/tokens";
import ConfirmDialog from "../common/ConfirmDialog";
import {
  Field,
  ModalHeader,
  fieldInputStyle,
  modalShellSx,
  modalFooterSx,
} from "../common/ModalKit";
import ComboImageField, { isAllowedComboImage } from "./ComboImageField";

const DEFAULT_IMG = `/assets/images/default-img.png`;

// Saved combo pictures are served off the API host, same as everywhere else
// in the admin app.
const storedImageSrc = (path) =>
  path ? `https://app.noutfermeture.com/api/${path}` : DEFAULT_IMG;

const UpdateCombo = ({
  isOpen,
  onClose,
  isUpdate,
  availableProducts,
  comboInfo,
}) => {
  const [formData, setFormData] = useState({
    name: "",
    selectedProducts: [],
  });

  const [showImg, setShowImg] = useState(DEFAULT_IMG);
  const [modalImage, setModalImage] = useState(null);
  const [showDimensionImg, setShowDimensionImg] = useState(DEFAULT_IMG);
  const [dimensionImage, setDimensionImage] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  // Pre-fill data when the modal opens or comboInfo changes
  useEffect(() => {
    if (isOpen && comboInfo) {
      setFormData({
        name: comboInfo.name || "",
        // Map the list to match our selection structure
        selectedProducts: comboInfo.list || [],
      });
      // Show the existing images from the database
      setShowImg(storedImageSrc(comboInfo.image));
      setShowDimensionImg(storedImageSrc(comboInfo.dimensionImage));
      setModalImage(null);
      setDimensionImage(null);
    }
  }, [isOpen, comboInfo]);

  // Both pickers behave the same way — only the slot they fill differs.
  const pickImage = (e, setFile, setPreview) => {
    const selectedImage = e.target.files[0];
    if (!selectedImage) return;

    if (!isAllowedComboImage(selectedImage)) {
      toast.error("Only JPG, JPEG or PNG images are allowed");
      e.target.value = "";
      return;
    }

    setFile(selectedImage);
    setPreview(URL.createObjectURL(selectedImage));
  };

  const handleAddProduct = (event, newValue) => {
    if (newValue) {
      setFormData({
        ...formData,
        selectedProducts: [...formData.selectedProducts, newValue],
      });
    }
  };

  const removeItem = (id) => {
    setFormData({
      ...formData,
      selectedProducts: formData.selectedProducts.filter((p) => p.id !== id),
    });
  };

  const handleUpdateSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name.trim() || formData.selectedProducts.length === 0) {
      toast.error("Name and at least one product are required");
      return;
    }

    const formDataAppend = new FormData();
    formDataAppend.append("id", comboInfo._id); // Include the ID for the update query
    formDataAppend.append("name", formData.name);
    formDataAppend.append("list", JSON.stringify(formData.selectedProducts));

    // Only append images if new ones were actually picked
    if (modalImage) {
      formDataAppend.append("image", modalImage);
    }
    if (dimensionImage) {
      formDataAppend.append("dimensionImage", dimensionImage);
    }

    try {
      const resp = await updateCombo({ formDataAppend, id: comboInfo._id });
      if (resp && (resp.status === 200 || resp.status === 201)) {
        toast.success("Combo updated successfully");
        isUpdate(); // Trigger refresh in parent
        onClose();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Error updating combo");
    }
  };

  return (
    <>
      <Modal open={isOpen} onClose={onClose}>
        <Box sx={modalShellSx(760)}>
          <ModalHeader title="Update Combo" subtitle={comboInfo?.name || ""} onClose={onClose} />

          <form onSubmit={handleUpdateSubmit}>
            <Box sx={{ padding: "22px", display: "grid", gridTemplateColumns: "210px minmax(0,1fr)", gap: "24px" }}>
              <Box sx={{ display: "flex", flexDirection: "column", gap: "22px" }}>
                <ComboImageField
                  label="Display image"
                  hint="Shown on the combo card in the catalogue. JPG, JPEG or PNG on a white background, 600px wide or more."
                  previewSrc={showImg}
                  onFileChange={(e) => pickImage(e, setModalImage, setShowImg)}
                />
                <ComboImageField
                  label="Dimension image"
                  hint="Shown inside the quotation modal while dimensions are entered. JPG, JPEG or PNG."
                  previewSrc={showDimensionImg}
                  onFileChange={(e) =>
                    pickImage(e, setDimensionImage, setShowDimensionImg)
                  }
                />
              </Box>

              <Box sx={{ display: "flex", flexDirection: "column", gap: "18px" }}>
                <Field label="Combo name">
                  <input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    style={fieldInputStyle}
                  />
                </Field>

                <Field label="Search designations to add">
                  <Autocomplete
                    options={availableProducts || []}
                    getOptionLabel={(option) => `${option.name} (${option.category || "N/A"})`}
                    onChange={handleAddProduct}
                    renderInput={(params) => <TextField {...params} size="small" />}
                  />
                </Field>

                <Field label={`Included designations (${formData.selectedProducts.length})`}>
                  <Box sx={{ border: `1px solid ${COLORS.cardBorder}`, borderRadius: "6px", maxHeight: "180px", overflowY: "auto" }}>
                    {formData.selectedProducts.length === 0 && (
                      <Box sx={{ padding: "12px", fontSize: "13px", color: COLORS.textFaint }}>
                        No products in this combo yet.
                      </Box>
                    )}
                    {formData.selectedProducts.map((item) => (
                      <Box
                        key={item.id}
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          padding: "8px 12px",
                          borderBottom: `1px solid ${COLORS.rowBorder}`,
                          "&:last-of-type": { borderBottom: "none" },
                        }}
                      >
                        <Box sx={{ fontSize: "13.5px", color: COLORS.textPrimary }}>{item.name}</Box>
                        <IconButton size="small" color="error" onClick={() => removeItem(item.id)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    ))}
                  </Box>
                </Field>
              </Box>
            </Box>

            <Box sx={modalFooterSx}>
              <Button sx={buttonSx.danger("44px")} onClick={() => setDeleteConfirmOpen(true)}>
                Delete Combo
              </Button>
              <Box sx={{ flex: 1 }} />
              <Button sx={buttonSx.neutral("44px")} onClick={onClose}>Cancel</Button>
              <Button type="submit" sx={{ ...buttonSx.primary(), fontWeight: 700 }}>Save Changes</Button>
            </Box>
          </form>
        </Box>
      </Modal>

      <ConfirmDialog
        open={deleteConfirmOpen}
        title="Delete combo"
        message={comboInfo ? `${comboInfo.name} will be permanently removed.` : ""}
        confirmLabel="Delete"
        cancelLabel="Keep it"
        onConfirm={() => {
          deleteCombo(comboInfo._id);
          setDeleteConfirmOpen(false);
          onClose();
          isUpdate();
        }}
        onCancel={() => setDeleteConfirmOpen(false)}
      />
    </>
  );
};

export default UpdateCombo;
